"use client"

import React, { useState } from "react";
import {
  Paper,
  Table,
  Group,
  Text,
  Pagination,
  Loader,
  ScrollArea,
  Select,
} from "@mantine/core";

export interface Column<T> {
  key: keyof T | string;
  label: string;
  render?: (value: any, item: T) => React.ReactNode;
  sortable?: boolean;
  width?: string | number;
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  loading?: boolean;
  itemsPerPage?: number;
  onRowClick?: (item: T) => void;
  emptyMessage?: string;
  compact?: boolean;
  loadingMessage?: string;
  showingText?: string;
  ofText?: string;
  // External pagination support (for server-side pagination)
  totalCount?: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  // External sorting support
  onSort?: (column: string, order: "asc" | "desc") => void;
  // Page size options
  pageSizeOptions?: number[];
  showPageSizeSelector?: boolean;
  itemsPerPageText?: string;
}

export function DataTable<T extends { id: string }>({
  data,
  columns,
  loading = false,
  itemsPerPage = 20,
  onRowClick,
  emptyMessage,
  compact = false,
  loadingMessage,
  showingText,
  ofText,
  totalCount,
  currentPage,
  onPageChange,
  onPageSizeChange,
  onSort,
  pageSizeOptions = [10, 20, 50, 100],
  showPageSizeSelector = false,
  itemsPerPageText,
}: DataTableProps<T>) {
  const [page, setPage] = useState(currentPage || 1);
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Use external pagination if provided, otherwise use internal
  const isExternalPagination = totalCount !== undefined && onPageChange !== undefined;
  const totalPages = isExternalPagination
    ? Math.ceil(totalCount / itemsPerPage)
    : Math.ceil(data.length / itemsPerPage);

  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  // Handle page changes
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    if (onPageChange) {
      onPageChange(newPage);
    }
  };

  // Handle sorting
  const handleSort = (columnKey: string) => {
    const newOrder = sortBy === columnKey && sortOrder === "asc" ? "desc" : "asc";

    if (onSort) {
      // External sorting
      onSort(columnKey, newOrder);
    } else {
      // Internal sorting
      if (sortBy === columnKey) {
        setSortOrder(sortOrder === "asc" ? "desc" : "asc");
      } else {
        setSortBy(columnKey);
        setSortOrder("asc");
      }
    }
  };

  // Process data for display
  let displayData = [...data];

  // Apply internal sorting if no external sorting is provided
  if (!onSort && sortBy) {
    displayData.sort((a, b) => {
      const aValue = (a as any)[sortBy];
      const bValue = (b as any)[sortBy];

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortOrder === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
      }

      if (aValue instanceof Date && bValue instanceof Date) {
        return sortOrder === "asc"
          ? aValue.getTime() - bValue.getTime()
          : bValue.getTime() - aValue.getTime();
      }

      return 0;
    });
  }

  // Apply internal pagination if no external pagination is provided
  const paginatedData = isExternalPagination
    ? displayData
    : displayData.slice(startIndex, endIndex);

  const getValue = (item: T, key: keyof T | string): any => {
    if (typeof key === "string" && key.includes(".")) {
      // Handle nested properties like "geoLocation.lat"
      return key.split(".").reduce((obj, prop) => obj?.[prop], item as any);
    }
    return (item as any)[key];
  };

  if (loading) {
    const content = (
      <Group justify="center" p={compact ? "md" : "xl"}>
        <Loader size={compact ? "md" : "lg"} />
        {loadingMessage && <Text>{loadingMessage}</Text>}
      </Group>
    );

    return compact ? (
      content
    ) : (
      <Paper withBorder radius="md">
        {content}
      </Paper>
    );
  }

  if (data.length === 0) {
    const content = (
      <Group justify="center" p={compact ? "md" : "xl"}>
        {emptyMessage && (
          <Text className="text-gray-500 dark:text-gray-400">{emptyMessage}</Text>
        )}
      </Group>
    );

    return compact ? (
      content
    ) : (
      <Paper withBorder radius="md">
        {content}
      </Paper>
    );
  }

  const tableContent = (
    <>
      <ScrollArea>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              {columns.map((column) => (
                <Table.Th
                  key={String(column.key)}
                  style={{
                    width: column.width,
                    cursor: column.sortable ? "pointer" : "default"
                  }}
                  className={column.sortable ? "fs-hover-surface select-none" : ""}
                  onClick={() =>
                    column.sortable && handleSort(String(column.key))
                  }
                >
                  <Group gap="xs" justify="space-between">
                    <Text size={compact ? "xs" : "sm"} fw={500}>
                      {column.label}
                    </Text>
                    {column.sortable && sortBy === String(column.key) && (
                      <Text size="xs" className="text-gray-400">
                        {sortOrder === "asc" ? "↑" : "↓"}
                      </Text>
                    )}
                  </Group>
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {paginatedData.map((item) => (
              <Table.Tr
                key={item.id}
                className={onRowClick ? "cursor-pointer fs-hover-surface" : ""}
                role={onRowClick ? "button" : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                onClick={() => onRowClick?.(item)}
                onKeyDown={(event) => {
                  if (!onRowClick) return
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault()
                    onRowClick(item)
                  }
                }}
              >
                {columns.map((column) => (
                  <Table.Td key={String(column.key)}>
                    {column.render
                      ? column.render(getValue(item, column.key), item)
                      : String(getValue(item, column.key) || "—")}
                  </Table.Td>
                ))}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea>

      {/* Pagination */}
      {totalPages > 1 && !compact && (
        <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <Group justify="space-between" p="md">
            <Group gap="md">
              {showingText && ofText && (
                <Text size="sm" className="text-gray-600 dark:text-gray-400">
                  {showingText} {startIndex + 1}-{Math.min(endIndex, isExternalPagination ? totalCount! : data.length)}{" "}
                  {ofText} {isExternalPagination ? totalCount : data.length}
                </Text>
              )}

              {showPageSizeSelector && onPageSizeChange && itemsPerPageText && (
                <Group gap="xs">
                  <Text size="sm" className="text-gray-600 dark:text-gray-400">
                    {itemsPerPageText}
                  </Text>
                  <Select
                    size="xs"
                    value={itemsPerPage.toString()}
                    onChange={(value) => value && onPageSizeChange(parseInt(value))}
                    data={pageSizeOptions.map(size => ({ value: size.toString(), label: size.toString() }))}
                    w={70}
                  />
                </Group>
              )}
            </Group>

            <Pagination
              total={totalPages}
              value={page}
              onChange={handlePageChange}
              size="sm"
              withEdges
              siblings={1}
              boundaries={1}
            />
          </Group>
        </div>
      )}
    </>
  );

  return compact ? (
    tableContent
  ) : (
    <Paper withBorder radius="md">
      {tableContent}
    </Paper>
  );
}
